// Voice guidance for the face-scan flow.
// Cues are loaded language-wise from the backend voice API
// (GET /res/get_voice_audio?lang=xx -> { files: { <cue>: <mp3 url> } }).
// The English set bundled in /public/audio is used as an offline / API-down
// fallback so playback never goes fully silent.

import storage from '../Assets/js/Global';
import { urls } from '../Url';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const COMPANY_KEY = process.env.REACT_APP_COMPANY_ID || '';

// Canonical cue -> filename. Keep in sync with the backend
// (resourceModel::getVoiceAudioManifest). Drives the bundled English fallback.
const AUDIO_FILES = {
  'scan-start': 'voice_scan_start.mp3',
  'mid-progress': 'voice_mid_progress.mp3',
  'almost-done': 'voice_almost_done.mp3',
  'scan-done': 'voice_scan_done.mp3',
  'face-locked': 'voice_face_locked.mp3',
  'no-face': 'voice_no_face.mp3',
  'lighting': 'voice_low_light.mp3',
  'bright': 'voice_bright.mp3',
  'motion': 'voice_motion.mp3',
  'out-of-frame': 'voice_out_of_frame.mp3',
  'too-close': 'voice_too_close.mp3',
  'too-far': 'voice_too_far.mp3',
  'multiple-faces': 'voice_multiple_faces.mp3',
  'device-movement': 'voice_device_movement.mp3',
  'quality-low': 'voice_quality_low.mp3',
};

// Bundled English audio (shipped in /public/audio) — the offline fallback,
// used until/unless the language pool loads from the API.
const fallbackPool = {};
// Active language pool fetched from the API, keyed by cue. Overlays fallbackPool.
let pool = {};
let loadedLang = null;
let loadingPromise = null;

let currentSound = null;
const lastSpokenAt = {};
let unlocked = false;
let muted = false;

// Built lazily on first cue lookup so merely importing this module (e.g. on the
// login/language screens) costs nothing — no Audio elements, no network warm-up.
let fallbackPoolBuilt = false;
function ensureFallbackPool() {
  if (fallbackPoolBuilt) return;
  fallbackPoolBuilt = true;
  Object.entries(AUDIO_FILES).forEach(([key, file]) => {
    try {
      const a = new Audio(`${PUBLIC_URL}/audio/${file}`);
      a.preload = 'auto';
      // 'metadata' is enough to know the file exists; the browser fetches the
      // full buffer on first play.
      a.load();
      fallbackPool[key] = a;
    } catch (e) {
      console.warn(`[speech] failed to construct fallback Audio for ${file}:`, e);
    }
  });
}

// Pick the language cue if loaded, otherwise the bundled English fallback.
function soundFor(key) {
  ensureFallbackPool();
  return pool[key] || fallbackPool[key];
}

// Per-language manifest cache: lang -> Promise<manifest | null>. Shared by the
// scan pool and single-cue lookups (e.g. getVoiceUrl) so each language is
// fetched at most once.
const manifestCache = {};

function fetchManifest(lang) {
  const target = String(lang || storage.get('selectedLanguage') || 'en').toLowerCase();
  if (manifestCache[target]) return manifestCache[target];
  manifestCache[target] = fetch(urls.getVoiceAudioList, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyKey: COMPANY_KEY, lang: target }),
  })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((data) => {
      if (!data || data.status !== true || !data.files) {
        throw new Error('malformed voice manifest');
      }
      return data;
    })
    .catch((err) => {
      console.warn(`[speech] voice manifest load failed for "${target}":`, err?.message || err);
      delete manifestCache[target]; // permit a retry later
      return null;
    });
  return manifestCache[target];
}

// Resolve a single cue's URL for a language — used by non-pool players such as
// the campaign invite audio. Resolves to null if the cue/language is
// unavailable so callers can fall back to a bundled file.
export function getVoiceUrl(key, lang) {
  return fetchManifest(lang).then((data) => (data && data.files ? data.files[key] || null : null));
}

// Warm the per-language voice manifest cache (URLs only — no Audio elements).
// Idempotent per language via manifestCache: safe to call on every language
// change; it hits the API only the first time a given language is requested.
// Call this once after login / on language change so screens that use voice
// audio (campaign invite, scan cues) resolve instantly from cache instead of
// paying the get_voice_audio_files round-trip on mount.
export function prefetchVoiceManifest(lang) {
  return fetchManifest(lang);
}

// Fetch a language's voice manifest and build its Audio pool for the scan flow.
// Idempotent per language and safe to call repeatedly (e.g. on every scan-screen
// mount). On any failure it keeps the existing pool — bundled English still
// covers playback — and allows a later retry for that language.
export function loadVoicesForLanguage(lang) {
  const target = String(lang || storage.get('selectedLanguage') || 'en').toLowerCase();
  if (target === loadedLang) return loadingPromise || Promise.resolve();
  loadedLang = target;
  loadingPromise = fetchManifest(target).then((data) => {
    if (!data || !data.files) {
      // Load failed — permit a retry on the next call for this language.
      if (loadedLang === target) loadedLang = null;
      return;
    }
    const next = {};
    Object.entries(data.files).forEach(([key, url]) => {
      try {
        const a = new Audio(url);
        a.preload = 'auto';
        a.load();
        next[key] = a;
      } catch (e) {
        console.warn(`[speech] failed to construct Audio for ${url}:`, e);
      }
    });
    pool = next;
  });
  return loadingPromise;
}

// Kick off the language voice load in the background, off the critical path.
// Uses requestIdleCallback so it never competes with in-flight API calls or the
// initial render; falls back to a deferred timeout where unsupported. Callers on
// the scan screen use this instead of a synchronous load so voices warm up
// lazily without slowing the page down.
export function loadVoicesInBackground(lang) {
  const run = () => loadVoicesForLanguage(lang);
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 3000 });
  } else {
    setTimeout(run, 0);
  }
}

// 1ms of silent WAV — used as a dummy audio that we play during the first
// user gesture to satisfy the browser's autoplay gate without touching the
// real pool elements (which would race with a subsequent speak() call).
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAVFYAAFRWAAABAAgAZGF0YQAAAAA=';
let primerAudio = null;

// Browsers block audio until a user gesture. Call this from a click handler
// once — playing a silent dummy unlocks audio for the page on
// Chrome/Firefox/modern Safari. Pool elements are NOT touched here, so a
// follow-up speak() call won't race with an in-flight priming play().
export function unlockAudio() {
  // Make sure the current language's cues are loading by the time we scan.
  loadVoicesForLanguage();
  if (unlocked) return;
  unlocked = true;
  try {
    if (!primerAudio) {
      primerAudio = new Audio(SILENT_WAV);
      primerAudio.preload = 'auto';
    }
    primerAudio.muted = false;
    primerAudio.volume = 0;
    const p = primerAudio.play();
    if (p && typeof p.then === 'function') {
      p.then(() => {
        try { primerAudio.pause(); primerAudio.currentTime = 0; } catch { }
      }).catch(() => { /* ignore — first speak() will retry the gesture */ });
    }
  } catch { }
}

export function setSpeechMuted(next) {
  muted = !!next;
  if (muted) {
    try { currentSound?.pause?.(); } catch { }
    currentSound = null;
  }
}

export function isSpeechMuted() {
  return muted;
}

// Speak a cue. `key` is one of AUDIO_FILES keys. `throttleMs=0` means play now.
// Otherwise we suppress the same key if it played within the window.
export function speak(_message, key, throttleMs = 6000) {
  if (muted) return;
  const snd = soundFor(key);
  if (!snd) return;

  const now = Date.now();
  if (throttleMs > 0 && lastSpokenAt[key] && now - lastSpokenAt[key] < throttleMs) return;
  lastSpokenAt[key] = now;

  try {
    if (currentSound && currentSound !== snd) {
      try { currentSound.pause(); currentSound.currentTime = 0; } catch { }
    }
    currentSound = snd;
    snd.pause();
    snd.currentTime = 0;
    // Defend against unlockAudio's in-flight priming leaving the element muted
    // when speak() runs in the same event/microtask cycle.
    snd.muted = false;
    snd.volume = 1;
    const p = snd.play();
    if (p && typeof p.catch === 'function') {
      p.catch((err) => {
        // Common: NotAllowedError before user gesture. Silent — caller can
        // surface a "tap to enable sound" affordance instead.
        if (err?.name !== 'NotAllowedError') {
          console.warn(`[speech] playback failed: ${key}`, err?.message || err);
        }
      });
    }
  } catch (e) {
    console.warn(`[speech] speak error for ${key}:`, e);
  }
}

// Same as speak() but invokes onDone when playback ends (or fails to start).
// Used for the scan-complete handoff so the results screen waits for the
// "scan complete" voice line.
export function speakThen(key, onDone) {
  if (muted) { onDone(); return; }
  const snd = soundFor(key);
  if (!snd) { onDone(); return; }
  try {
    if (currentSound && currentSound !== snd) {
      try { currentSound.pause(); currentSound.currentTime = 0; } catch { }
    }
    currentSound = snd;
    snd.pause();
    snd.currentTime = 0;
    snd.muted = false;
    snd.volume = 1;
    const handle = () => {
      snd.removeEventListener('ended', handle);
      snd.removeEventListener('error', handle);
      onDone();
    };
    snd.addEventListener('ended', handle);
    snd.addEventListener('error', handle);
    const p = snd.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        snd.removeEventListener('ended', handle);
        snd.removeEventListener('error', handle);
        onDone();
      });
    }
  } catch (e) {
    console.warn(`[speech] speakThen error for ${key}:`, e);
    onDone();
  }
}

export function resetSpeech() {
  try { currentSound?.pause?.(); } catch { }
  currentSound = null;
  Object.keys(lastSpokenAt).forEach((k) => delete lastSpokenAt[k]);
}
